// src/components/ReturnsMigratorAllToV2.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import { db } from "../firebase/firebaseConfig";

/**
 * ONE migrator to bring ALL legacy returns/parts + drafts + archives into returns_v2.
 *
 * Sources:
 *  - returns (cyl final)
 *  - returns_draft (cyl draft)
 *  - returns_archive (cyl archived)
 *  - returns_parts (parts final)
 *  - returns_parts_draft (parts draft)
 *  - returns_parts_archive (parts archived)
 *
 * Destination:
 *  - returns_v2
 *
 * Behavior:
 *  - archives => archive:true, archivedAt existing if available else serverTimestamp
 *  - non-archives => archive:false, archivedAt:null
 *  - rebuilds searchTokens/searchText/searchUpdatedAt
 *  - does NOT delete old docs
 */

const DEST_COL = "returns_v2";

const SOURCES = [
  { key: "returns", col: "returns", kind: "cyl", isArchive: false, label: "returns (cyl)" },
  { key: "returns_draft", col: "returns_draft", kind: "cyl", isArchive: false, label: "returns_draft (cyl draft)" },
  { key: "returns_archive", col: "returns_archive", kind: "cyl", isArchive: true, label: "returns_archive (cyl archived)" },

  { key: "returns_parts", col: "returns_parts", kind: "parts", isArchive: false, label: "returns_parts (parts)" },
  { key: "returns_parts_draft", col: "returns_parts_draft", kind: "parts", isArchive: false, label: "returns_parts_draft (parts draft)" },
  { key: "returns_parts_archive", col: "returns_parts_archive", kind: "parts", isArchive: true, label: "returns_parts_archive (parts archived)" },
];

// ---------- small helpers ----------
function safeId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return "id_" + Math.random().toString(16).slice(2) + "_" + Date.now();
}

function norm(v) {
  return String(v || "").trim().toLowerCase();
}
function stripNonAlnumToSpaces(s) {
  return norm(s).replace(/[^a-z0-9]+/g, " ").trim();
}
function stripDigits(s) {
  return String(s || "").replace(/\D+/g, "");
}
const STOPWORDS = new Set([
  "a","an","the","and","or","to","of","in","on","for","with","at","by","from","as","is","it","be","was","were",
  "this","that","these","those","are","am","i","you","we","they","he","she","my","your","our","their"
]);

function addPrefixes(set, raw, minLen = 3, maxLen = 12) {
  const s = norm(raw).replace(/\s+/g, "");
  if (!s) return;
  const capped = s.slice(0, maxLen);
  for (let i = minLen; i <= capped.length; i++) set.add(capped.slice(0, i));
}

// Returned parts logic (same as you already use)
const INDIRECT_PARTS = ["Inlet Group", "Tundish", "Expansion Vessel", "2 Port Valve", "Dual Stat", "Immersion"];
const DIRECT_PARTS = ["Inlet Group", "Tundish", "Expansion Vessel", "Smart Immersion", "Immersion"];
function getSystemList(systemType) {
  if (systemType === "indirect") return INDIRECT_PARTS;
  if (systemType === "direct") return DIRECT_PARTS;
  return [];
}
function buildReturnedParts(parts) {
  if (!parts) return [];
  if (parts.noPartsReturned) return [];
  const base = getSystemList(parts.systemType);
  if (parts.allPartsReturned) return [...base];
  const chosenBase = Object.entries(parts.partialBaseSelection || {})
    .filter(([, v]) => !!v)
    .map(([name]) => name);
  const extra = (parts.additionalParts || []).map((x) => String(x).trim()).filter(Boolean);
  return [...chosenBase, ...extra];
}

/**
 * Tight search index builder that works for BOTH cyl + parts because it looks at:
 *   v2.cyl.form OR v2.parts.form
 */
function buildSearchIndexForV2(v2) {
  const tokens = new Set();

  const type = norm(v2?.type);     // "cyl" | "parts"
  const status = norm(v2?.status); // whatever you store

  const f = v2?.cyl?.form || v2?.parts?.form || {};
  const returnedParts = v2?.cyl?.returnedParts || [];

  if (type) tokens.add(`type:${type}`);
  if (status) tokens.add(`status:${status}`);

  // RN / Serial / SRET
  const rn = norm(f.returnsNoteNumber);
  const rnDigits = stripDigits(rn);
  if (rn) tokens.add(rn);
  if (rnDigits) {
    tokens.add(rnDigits);
    tokens.add(`rn${rnDigits}`);
    addPrefixes(tokens, rnDigits, 4, 10);
  }

  const serial = norm(f.serialNumber);
  const serialDigits = stripDigits(serial);
  if (serial) tokens.add(serial);
  if (serialDigits) {
    tokens.add(serialDigits);
    addPrefixes(tokens, serialDigits, 4, 12);
  }

  const sret = norm(f.sretNumber);
  const sretDigits = stripDigits(sret);
  if (sret) tokens.add(sret);
  if (sretDigits) {
    tokens.add(sretDigits);
    addPrefixes(tokens, sretDigits, 4, 12);
  }

  // Model (cyl)
  const model = stripNonAlnumToSpaces(f.cylinderModel);
  if (model) {
    for (const w of model.split(/\s+/)) {
      if (!w || w.length < 2) continue;
      if (STOPWORDS.has(w)) continue;
      tokens.add(w);
    }
    const modelCompact = norm(f.cylinderModel).replace(/[^a-z0-9]+/g, "");
    if (modelCompact) {
      tokens.add(modelCompact);
      addPrefixes(tokens, modelCompact, 4, 14);
    }
  }

  // partCode (parts)
  const partCodeCompact = norm(f.partCode).replace(/[^a-z0-9]+/g, "");
  if (partCodeCompact) {
    tokens.add(partCodeCompact);
    addPrefixes(tokens, partCodeCompact, 3, 14);
  }

  // codes (exact)
  ["reasonCode","reasonDefectCode","faultFoundCode","faultDefectCode","reportedCode"].forEach((k) => {
    const val = norm(f[k]);
    if (val) tokens.add(val);
  });

  // returned parts (words only)
  if (returnedParts.length) {
    const rp = stripNonAlnumToSpaces(returnedParts.join(" "));
    for (const w of rp.split(/\s+/)) {
      if (!w || w.length < 2) continue;
      if (STOPWORDS.has(w)) continue;
      tokens.add(w);
    }
  }

  const searchTokens = Array.from(tokens).filter(Boolean).slice(0, 250);
  const searchText = searchTokens.join(" • ");
  return { searchTokens, searchText };
}

function coerceStatusUpdatedAt(v) {
  // old drafts sometimes store ISO string
  if (!v) return null;
  if (typeof v === "string") {
    const d = new Date(v);
    if (!isNaN(d.getTime())) return v; // keep as string to avoid Firestore Timestamp import hell
    return null;
  }
  // Timestamp or whatever else -> keep as-is
  return v;
}

/**
 * Convert legacy CYL documents:
 * - returns / returns_archive: data.payload contains form/parts/leaks/cylCheck/returnedParts (per your example)
 * - returns_draft: form/parts/leaks/cylCheck at top level (per your example)
 */
function buildV2FromLegacyCyl({ src, srcDocId, data }) {
  const isArchive = !!src.isArchive;

  const payload = data.payload || {}; // returns + returns_archive
  const form = payload.form || data.form || {};
  const parts = payload.parts || data.parts || {};
  const leaks = payload.leaks || data.leaks || {};
  const cylCheck = payload.cylCheck || data.cylCheck || {};

  const returnedParts =
    (payload.returnedParts || data.returnedParts) ||
    buildReturnedParts(parts);

  const createdAt = data.createdAt || serverTimestamp();
  const updatedAt = data.updatedAt || serverTimestamp();

  const archivedAt =
    isArchive ? (data.archivedAt || data.notesUpdatedAt || serverTimestamp()) : null;

  const v2 = {
    type: "cyl",
    status: data.status || "new",
    statusUpdatedAt: coerceStatusUpdatedAt(data.statusUpdatedAt) || null,

    createdAt,
    updatedAt,

    submittedAt: data.submittedAt || null,
    adminNotes: data.adminNotes || "",
    notesUpdatedAt: data.notesUpdatedAt || null,

    // ✅ unified archive flags
    archive: isArchive,
    archivedAt,

    source: data.source || "pthelpdesk",
    version: 1, // new v2 schema version

    legacyRefs: {
      fromCollection: src.col,
      oldIdField: data.id || null,
      draftRef: data.draftRef || null,
      finalRefId: data.finalRefId || null,
    },

    migratedFrom: {
      collection: src.col,
      docId: srcDocId,
      migratedAt: serverTimestamp(),
    },

    cyl: {
      form,
      parts,
      leaks,
      cylCheck,
      returnedParts,
    },
  };

  // carry storage if present (drafts often have it)
  if (data.storage?.folderId) {
    v2.storage = {
      provider: data.storage.provider || "sharepoint",
      folderId: data.storage.folderId,
      folderUrl: data.storage.folderUrl || null,
    };
  }

  // search fields
  const { searchTokens, searchText } = buildSearchIndexForV2(v2);
  v2.searchTokens = searchTokens;
  v2.searchText = searchText;
  v2.searchUpdatedAt = serverTimestamp();

  return v2;
}

/**
 * Convert legacy PARTS documents:
 * - returns_parts / returns_parts_archive: looks like payload.form + payload.templateSnapshot + payload.test
 * - returns_parts_draft: top-level form/template/test (per your example)
 */
function buildV2FromLegacyParts({ src, srcDocId, data }) {
  const isArchive = !!src.isArchive;

  const payload = data.payload || data; // cover both shapes
  const form = payload.form || {};
  const templateSnapshot = payload.templateSnapshot || payload.template || data.templateSnapshot || data.template || {};
  const test = payload.test || data.test || {};

  const createdAt = data.createdAt || serverTimestamp();
  const updatedAt = data.updatedAt || serverTimestamp();

  const archivedAt =
    isArchive ? (data.archivedAt || data.notesUpdatedAt || serverTimestamp()) : null;

  const v2 = {
    type: "parts",
    status: data.status || "new",
    statusUpdatedAt: coerceStatusUpdatedAt(data.statusUpdatedAt) || null,

    createdAt,
    updatedAt,

    submittedAt: data.submittedAt || null,
    adminNotes: data.adminNotes || "",
    notesUpdatedAt: data.notesUpdatedAt || null,

    archive: isArchive,
    archivedAt,

    source: data.source || "pthelpdesk",
    version: 1,

    legacyRefs: {
      fromCollection: src.col,
      oldIdField: data.id || null,
      draftRef: data.draftRef || null,
      finalRefId: data.finalRefId || null,
    },

    migratedFrom: {
      collection: src.col,
      docId: srcDocId,
      migratedAt: serverTimestamp(),
    },

    parts: {
      form,
      templateSnapshot,
      test,
    },
  };

  if (data.storage?.folderId) {
    v2.storage = {
      provider: data.storage.provider || "sharepoint",
      folderId: data.storage.folderId,
      folderUrl: data.storage.folderUrl || null,
    };
  }

  const { searchTokens, searchText } = buildSearchIndexForV2(v2);
  v2.searchTokens = searchTokens;
  v2.searchText = searchText;
  v2.searchUpdatedAt = serverTimestamp();

  return v2;
}

function pickDisplay(v2) {
  const f = v2?.cyl?.form || v2?.parts?.form || {};
  return {
    rn: f.returnsNoteNumber || "",
    serial: f.serialNumber || "",
    model: f.cylinderModel || "",
    partCode: f.partCode || "",
  };
}

export default function ReturnsMigratorAllToV2() {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]); // unified list of source docs
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [log, setLog] = useState([]);
  const [err, setErr] = useState("");
  const [copyBusy, setCopyBusy] = useState(false);

  const addLog = (line) => setLog((p) => [line, ...p].slice(0, 400));
  const selectedCount = selectedIds.size;

  const loadAll = async () => {
    setErr("");
    setLoading(true);
    setRows([]);
    setSelectedIds(new Set());
    try {
      const all = [];

      for (const src of SOURCES) {
        addLog(`Loading ${src.col}...`);

        // Some collections might not have updatedAt; createdAt is safest
        // If a collection lacks createdAt on some docs, Firestore orderBy will still work if field exists in all fetched docs.
        // If you have any truly fieldless docs, drop orderBy and just limit.
        let snap;
        try {
          snap = await getDocs(query(collection(db, src.col), orderBy("createdAt", "desc"), limit(500)));
        } catch {
          snap = await getDocs(query(collection(db, src.col), limit(500)));
        }

        snap.docs.forEach((d) => {
          const data = d.data() || {};

          // Decide destination id:
          // - Prefer explicit "id" field if your archive docs store original id there
          // - Else use the source doc id
          const destId = (data.id && String(data.id)) || d.id;

          // Build preview v2 (cheap)
          const v2 = src.kind === "cyl"
            ? buildV2FromLegacyCyl({ src, srcDocId: d.id, data })
            : buildV2FromLegacyParts({ src, srcDocId: d.id, data });

          const disp = pickDisplay(v2);

          all.push({
            // unique row id for selection (source + doc)
            rowKey: `${src.col}__${d.id}`,
            src,
            srcDocId: d.id,
            destId,
            kind: src.kind,
            isArchive: src.isArchive,

            status: v2.status,
            rn: disp.rn,
            serial: disp.serial,
            model: disp.model,
            partCode: disp.partCode,

            createdAtISO: (data.createdAt?.toDate?.()?.toISOString?.() || ""),
            hasStorage: !!data.storage?.folderId,
          });
        });

        addLog(`Loaded ${snap.size} from ${src.col}.`);
      }

      // newest first (best-effort)
      all.sort((a, b) => (b.createdAtISO || "").localeCompare(a.createdAtISO || ""));
      setRows(all);
      addLog(`DONE: loaded ${all.length} docs across all sources.`);
    } catch (e) {
      console.error(e);
      setErr(e?.message || "Failed to load sources.");
    } finally {
      setLoading(false);
    }
  };

  const toggleSelect = (rowKey) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(rowKey)) next.delete(rowKey);
      else next.add(rowKey);
      return next;
    });
  };

  const selectAll = () => setSelectedIds(new Set(rows.map((r) => r.rowKey)));
  const clearAll = () => setSelectedIds(new Set());

  const migrateSelected = async () => {
    if (selectedCount === 0) {
      alert("Select at least one row first.");
      return;
    }

    if (!window.confirm(`Migrate ${selectedCount} doc(s) into ${DEST_COL}? (No deletes, writes/overwrites ${DEST_COL} docs)`)) {
      return;
    }

    setCopyBusy(true);
    setErr("");

    try {
      const list = rows.filter((r) => selectedIds.has(r.rowKey));

      for (let i = 0; i < list.length; i++) {
        const r = list[i];
        addLog(`(${i + 1}/${list.length}) Reading ${r.src.col}/${r.srcDocId}...`);

        const snap = await getDoc(doc(db, r.src.col, r.srcDocId));
        if (!snap.exists()) {
          addLog(`⚠️ Missing: ${r.src.col}/${r.srcDocId} (skipped)`);
          continue;
        }

        const data = snap.data() || {};

        // Ensure dest id is stable (prefer data.id if set)
        const destId = (data.id && String(data.id)) || r.destId || safeId();

        const payload =
          r.kind === "cyl"
            ? buildV2FromLegacyCyl({ src: r.src, srcDocId: r.srcDocId, data })
            : buildV2FromLegacyParts({ src: r.src, srcDocId: r.srcDocId, data });

        // Full overwrite write to returns_v2/{destId}
        await setDoc(doc(db, DEST_COL, destId), payload);

        addLog(`✅ Migrated ${r.src.col}/${r.srcDocId} -> ${DEST_COL}/${destId} (${r.kind}${r.isArchive ? ", archived" : ""})`);
      }

      addLog("DONE: migration complete.");
      alert("Migration complete.");
      clearAll();
    } catch (e) {
      console.error(e);
      setErr(e?.message || "Migration failed.");
    } finally {
      setCopyBusy(false);
    }
  };

  const summary = useMemo(() => {
    const by = {};
    rows.forEach((r) => {
      by[r.src.col] = (by[r.src.col] || 0) + 1;
    });
    return by;
  }, [rows]);

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <h1 className="text-3xl font-bold text-gray-900">Migrate ALL legacy returns → returns_v2</h1>

        <button
          type="button"
          onClick={loadAll}
          disabled={loading || copyBusy}
          className="px-3 py-2 rounded-md border border-gray-300 hover:bg-gray-50"
        >
          {loading ? "Loading…" : "Load all sources"}
        </button>

        <button
          type="button"
          onClick={selectAll}
          disabled={rows.length === 0 || copyBusy}
          className="px-3 py-2 rounded-md border border-gray-300 hover:bg-gray-50"
        >
          Select all
        </button>

        <button
          type="button"
          onClick={clearAll}
          disabled={selectedCount === 0 || copyBusy}
          className="px-3 py-2 rounded-md border border-gray-300 hover:bg-gray-50"
        >
          Clear
        </button>

        <button
          type="button"
          onClick={migrateSelected}
          disabled={selectedCount === 0 || copyBusy}
          className="px-4 py-2 rounded-md bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
        >
          {copyBusy ? "Migrating…" : `Migrate selected (${selectedCount})`}
        </button>

        <div className="ml-auto text-sm text-gray-600">
          Destination: <b>{DEST_COL}</b>
        </div>
      </div>

      {err && <div className="mb-4 text-sm text-red-600">{err}</div>}

      {rows.length > 0 ? (
        <div className="mb-4 text-xs text-gray-600">
          Loaded totals:{" "}
          {Object.entries(summary).map(([k, n]) => (
            <span key={k} className="inline-block mr-3">
              <b>{k}</b>: {n}
            </span>
          ))}
        </div>
      ) : null}

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_420px] gap-6">
        {/* LIST */}
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b bg-gray-50 flex items-center justify-between">
            <div className="text-sm text-gray-700">
              Loaded: <b>{rows.length}</b> · Selected: <b>{selectedCount}</b>
            </div>
            <div className="text-xs text-gray-500">Tick rows then migrate</div>
          </div>

          {rows.length === 0 ? (
            <div className="p-6 text-sm text-gray-600">
              Click <b>Load all sources</b>.
            </div>
          ) : (
            <div className="overflow-auto">
              <table className="w-full text-sm">
                <thead className="text-left bg-white sticky top-0">
                  <tr className="border-b">
                    <th className="p-3 w-10">✓</th>
                    <th className="p-3">Source</th>
                    <th className="p-3">Type</th>
                    <th className="p-3">RN</th>
                    <th className="p-3">Serial</th>
                    <th className="p-3">Model / Part</th>
                    <th className="p-3">Status</th>
                    <th className="p-3">Archived?</th>
                    <th className="p-3">Dest ID</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const checked = selectedIds.has(r.rowKey);
                    const title = r.kind === "cyl" ? (r.model || "—") : (r.partCode || "—");
                    return (
                      <tr key={r.rowKey} className="border-b hover:bg-gray-50">
                        <td className="p-3">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleSelect(r.rowKey)}
                            className="h-4 w-4"
                          />
                        </td>
                        <td className="p-3 text-xs">{r.src.col}</td>
                        <td className="p-3">{r.kind}</td>
                        <td className="p-3 font-medium">{r.rn || <span className="text-gray-400">—</span>}</td>
                        <td className="p-3">{r.serial || <span className="text-gray-400">—</span>}</td>
                        <td className="p-3">{title}</td>
                        <td className="p-3">{r.status || <span className="text-gray-400">—</span>}</td>
                        <td className="p-3">{r.isArchive ? "YES" : "no"}</td>
                        <td className="p-3 text-xs font-mono">{(r.destId || "").slice(-10)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* LOG */}
        <div className="space-y-6">
          <div className="bg-white border border-gray-200 rounded-xl p-4">
            <h2 className="text-lg font-semibold mb-3">Log</h2>
            {log.length === 0 ? (
              <div className="text-sm text-gray-600">Actions will appear here.</div>
            ) : (
              <div className="max-h-96 overflow-auto text-xs space-y-1">
                {log.map((l, i) => (
                  <div key={i} className="font-mono text-gray-700">{l}</div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-white border border-gray-200 rounded-xl p-4 text-xs text-gray-600">
            <div className="font-semibold mb-2">What this does</div>
            <ul className="list-disc pl-5 space-y-1">
              <li>Writes every migrated doc into <b>returns_v2</b>.</li>
              <li>Sets <b>archive: true</b> for <b>returns_archive</b> and <b>returns_parts_archive</b>.</li>
              <li>Sets <b>archive: false</b> for everything else.</li>
              <li>Rebuilds <b>searchTokens</b> + <b>searchText</b> + <b>searchUpdatedAt</b>.</li>
              <li>Does not delete old docs.</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
